/* ────────────────────────────────────────────────────────────────────────
   S A G A S   D E   A U T E N T I C A C I Ó N
   --------------------------------------------------------------------- */
import { all, fork, put, takeEvery, call } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';
import { authApiResponseSuccess, authApiResponseError,} from './actions'; 
import { APICore, setAuthorization } from '../../helpers/api/apiCore';
import {
  AuthActionTypes,
  LOGIN_USER_WITH_GOOGLE,
  LOGIN_USER_WITH_GOOGLE_SUCCESS,
  LOGIN_USER_WITH_GOOGLE_FAILURE,
} from './constants';


/* ──────────────── Tipos auxiliares ──────────────── */
interface LoginPayload     { username: string; password: string }
interface SignupPayload    { fullname: string; email: string; password: string }
interface GooglePayload    { token: string }
interface ForgotPwdPayload { email: string }

interface Action<T> { type: string; payload: T }

const api = new APICore();

/* ═════════════ 1. LOGIN ═════════════ */
function* login({ payload: { username, password } }: Action<LoginPayload>): SagaIterator {
  try {
    const base = process.env.REACT_APP_8081;
    const url  = `${base}/ApiRest/api/auth/login?identity=${encodeURIComponent(
      username
    )}&password=${encodeURIComponent(password)}`;

    const res: Response = yield call(fetch, url, {
      method: 'POST',
      headers: { Accept: '*/*', 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    });
    if (!res.ok) throw new Error('Usuario o contraseña incorrectos');

const { token, teamManagementToken, username: uname, email, idioma } = yield call([res, res.json]);

    const userData = { token, teamManagementToken, username: uname, email, idioma };
    api.setLoggedInUser(userData);
    setAuthorization(token);

    yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, userData));
    yield put({ type: 'SET_LANGUAGE', payload: idioma });
  } catch (err: any) {
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, err.message));
    api.setLoggedInUser(null);
    setAuthorization(null);
  }
}

/* ═════════════ 2. LOGIN CON GOOGLE ═════════════ */
function* loginWithGoogle({ payload: { token } }: Action<GooglePayload>): SagaIterator {
  try {
    const url4000 = process.env.REACT_APP_4000;
    const res: Response = yield call(fetch, `${url4000}/api/auth/google-login`, {
      method: 'POST',
      headers: { Accept: '*/*', 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
const { message } = yield call([res, res.json]);
      throw new Error(message || 'Error en el inicio de sesión con Google.');
    }
    const result = yield call([res, res.json]);
    const { email, idioma } = result.user;

    /* validar que exista en tu sistema */
    const base8081 = process.env.REACT_APP_8081;
    const chk: Response = yield call(
      fetch,
      `${base8081}/ApiRest/api/auth/check-email?email=${encodeURIComponent(email)}`,
      { method: 'GET', headers: { Accept: '*/*', 'ngrok-skip-browser-warning': 'true' } }
    );
const { exists, username } = yield call([chk, chk.json]);
    if (!exists) throw new Error('Usuario no encontrado en el sistema.');

    const userData = { token: result.token, username, email };
    api.setLoggedInUser(userData);
    setAuthorization(result.token);

    yield put(authApiResponseSuccess(LOGIN_USER_WITH_GOOGLE_SUCCESS, userData));
    yield put({ type: 'SET_LANGUAGE', payload: idioma });
  } catch (err: any) {
    yield put(authApiResponseError(LOGIN_USER_WITH_GOOGLE_FAILURE, err.message));
  }
}

/* ═════════════ 3. LOGOUT ═════════════ */
function* logout(): SagaIterator {
  try {
    api.setLoggedInUser(null);
    setAuthorization(null);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (err: any) {
    yield put(authApiResponseError(AuthActionTypes.LOGOUT_USER, err.message));
  }
}

/* ═════════════ 4. SIGN-UP ═════════════ */
function* signup({ payload }: Action<SignupPayload>): SagaIterator {
  try {
    /* aquí iría la llamada real a tu API */
    yield put({ type: AuthActionTypes.SIGNUP_USER_SUCCESS, payload: { userSignUp: true } });
  } catch (err: any) {
    yield put(authApiResponseError(AuthActionTypes.SIGNUP_USER, err.message));
  }
}

/* ═════════════ 5. FORGOT PASSWORD ═════════════ */
function* forgotPassword({ payload: { email } }: Action<ForgotPwdPayload>): SagaIterator {
  try {
    const base = process.env.REACT_APP_8081;
    const res: Response = yield call(
      fetch,
      `${base}/ApiRest/api/auth/password-change?email=${encodeURIComponent(email)}`,
      { method: 'POST', headers: { Accept: '*/*', 'ngrok-skip-browser-warning': 'true' } }
    );
    if (!res.ok) throw new Error('Email no encontrado');
    yield put({ type: AuthActionTypes.FORGOT_PASSWORD_SUCCESS, payload: { message: 'Instrucciones enviadas.' } });
  } catch (err: any) {
    yield put({ type: AuthActionTypes.FORGOT_PASSWORD_ERROR, payload: { error: err.message } });
  }
}

/* ═════════════ 6. WATCHERS ═════════════ */
function* watchLoginUser()           { yield takeEvery(AuthActionTypes.LOGIN_USER, login); }
function* watchLoginWithGoogle()     { yield takeEvery(LOGIN_USER_WITH_GOOGLE, loginWithGoogle); }
function* watchLogout()              { yield takeEvery(AuthActionTypes.LOGOUT_USER, logout); }
function* watchSignup()              { yield takeEvery(AuthActionTypes.SIGNUP_USER, signup); }

/* ═════════════ 7. ROOT SAGA ═════════════ */
export default function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogout),
    fork(watchSignup),
    fork(watchLoginWithGoogle),
  ]);
}
